import { Linkedin, FileText, Award } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQualiopi } from '@/hooks/useQualiopi';
import { QualiopiLogo } from '@/components/ui/QualiopiLogo';
import { FranceNumLogo } from '@/components/ui/FranceNumLogo';
import { ToolLogo } from '@/components/ui/ToolLogo';

export const SiteFooter = () => {
  const { t } = useTranslation();
  const { getComplaintFormUrl } = useQualiopi();
  const complaintUrl = getComplaintFormUrl();
  const location = useLocation();

  const isIndividualCoursePage =
    location.pathname.startsWith('/formation/') && location.pathname !== '/formations';

  return (
    <footer className="bg-slate-900 text-slate-300" aria-label="Pied de page">
      <div className="container-premium py-16">
        {/* Main grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-12">
          {/* Col 1 — Brand */}
          <div className="col-span-2 md:col-span-1 space-y-4">
            <div className="flex items-center gap-3">
              <img
                src="/images/nocodeo-icon.png"
                alt="NoCodeo"
                className="h-10 w-auto object-contain"
              />
              <span className="text-lg font-bold text-white">NoCodeo</span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              {t('footer.organisme')}
            </p>
            <p className="text-xs text-slate-500">N° : 11931186393</p>
            <a
              href="https://www.linkedin.com/company/nocodeo/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Suivre NoCodeo sur LinkedIn"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
            >
              <Linkedin className="h-4 w-4" />
              <span>{t('footer.followLinkedin')}</span>
            </a>
          </div>

          {/* Col 2 — Services */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              Services
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/conseil" className="footer-link">
                  Conseil & Automatisation
                </Link>
              </li>
              <li>
                <Link to="/formations" className="footer-link">
                  Formations
                </Link>
              </li>
              <li>
                <Link to="/tarifs" className="footer-link">
                  Tarifs
                </Link>
              </li>
              <li>
                <Link to="/financement" className="footer-link">
                  Financement OPCO
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3 — Ressources */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              Ressources
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/blog" className="footer-link">
                  {t('nav.blog')}
                </Link>
              </li>
              <li>
                <Link to="/cas-clients" className="footer-link">
                  Cas clients
                </Link>
              </li>
              <li>
                <Link to="/certifications-outils" className="footer-link">
                  Certifications outils
                </Link>
              </li>
              <li>
                <Link
                  to="/certificat-qualiopi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1"
                >
                  <FileText className="h-3 w-3" />
                  {t('footer.certificat')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4 — Legal */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider">
              Légal
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/mentions-legales" className="footer-link">
                  {t('footer.mentionsLegales')}
                </Link>
              </li>
              <li>
                <Link to="/conditions-generales-utilisation" className="footer-link">
                  {t('footer.cgu')}
                </Link>
              </li>
              <li>
                <Link to="/cgv" className="footer-link">
                  {t('footer.cgv')}
                </Link>
              </li>
              <li>
                <Link to="/politique-confidentialite" className="footer-link">
                  {t('footer.politiqueConfidentialite')}
                </Link>
              </li>
              <li>
                <Link to="/reglement-interieur" className="footer-link">
                  {t('footer.reglement')}
                </Link>
              </li>
              {complaintUrl && (
                <li>
                  <a
                    href={complaintUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-400 hover:text-amber-300 transition-colors font-medium"
                  >
                    {t('footer.reclamation')}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Left — Copyright */}
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} NoCodeo. {t('footer.disclaimer')}
            </p>

            {/* Right — Certifications & tools */}
            {!isIndividualCoursePage && (
              <div className="flex items-center gap-6">
                <QualiopiLogo size="sm" showMention={false} certificateUrl="/documents/certificat-qualiopi.pdf" />
                <FranceNumLogo size="sm" showMention={false} />
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-800/50">
                  <ToolLogo name="Airtable" size={20} />
                  <ToolLogo name="Softr" size={20} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};
